import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import LazyImage from '../../../components/display/LazyImage'
import Brief from '../../../components/landing/Brief'
import Header, {
  useLearnMoreClickHandler,
} from '../../../components/landing/Header'
import IllustratedSteps from '../../../components/landing/IllustratedSteps'
import InfoParagraph from '../../../components/landing/InfoParagraph'
import MasonryImages from '../../../components/landing/MasonryImages'
import SectionIntro from '../../../components/landing/SectionIntro'
import SinglePromoImage from '../../../components/landing/SinglePromoImage'
import StoriesCarousel from '../../../components/landing/StoriesCarousel'
import StoryCredit from '../../../components/landing/StoryCredit'
import ScrollTop from '../../../components/utils/ScrollTop'
import {
  Container,
  ImageColumn,
  PageCTALink,
  ResponsiveTwoColumnLayout,
  SpacedColumn,
} from '../../../components/landing/display'

import {
  benefitsImages,
  benefitsPromoImage,
  benefitsText,
  briefText,
  // faqItems,
  headerImage,
  headerText,
  howItWorksSteps,
  storiesForCarousel,
} from './content'

const StoriesLandingCustomer = () => {
  const { md: largerThanMd } = useMediaQueryContext()
  const briefEl = useRef<HTMLDivElement>()
  const headerImgEl = useRef<HTMLImageElement>()

  const [t] = useTranslation('spelling')

  const handleLearnMoreClick = useLearnMoreClickHandler({
    smallScreenTarget: headerImgEl,
    largeScreenTarget: briefEl,
  })

  return (
    <>
      <ScrollTop />
      <Header
        onCtaClick={handleLearnMoreClick}
        h1Text="Riparide Stories"
        h2Text={headerText.heading}
        h3Text={headerText.subheading}
      >
        <LazyImage
          ref={headerImgEl}
          data-src={headerImage.large.url}
          data-srcset={`
            ${headerImage.medium.url} ${headerImage.medium.width}w,
            ${headerImage.large.url} ${headerImage.large.width}w,
            ${headerImage.hero.url} ${headerImage.hero.width}w,
          `}
          preload
        />
      </Header>

      <Container ref={briefEl}>
        <Brief text={briefText} />

        <MasonryImages
          images={benefitsImages}
          imagesPerColumn={{
            1: 3,
            2: 2,
            3: 2,
          }}
          style={{
            marginTop: largerThanMd ? 120 : 40,
          }}
        />
      </Container>

      <Container>
        <SectionIntro
          primaryHeading="Escape the Ordinary"
          secondaryHeading={`${t('traveler')} Benefits`}
          paragraphText={`Discover advice from real ${t(
            'traveler',
          )}s to inspire your next escape.`}
        />
        <ResponsiveTwoColumnLayout>
          <ImageColumn>
            <SinglePromoImage
              src={benefitsPromoImage.large.url}
              data-src={`
                ${benefitsPromoImage.hero.url} ${benefitsPromoImage.hero.width}w,
                ${benefitsPromoImage.large.url} ${benefitsPromoImage.large.width}w
              `}
              annotationComp={
                <StoryCredit
                  avatar={{
                    thumbLg:
                      'https://assets.riparide.com/user/1443/avatar/thumb_lg-e3333569e4f1e5538ae0bec319be8e57.jpg',
                  }}
                  firstName="Jesse"
                  storyTitle="The Aire Down There"
                  storyUrl="/stories/91-the-aire-down-there"
                />
              }
            />
          </ImageColumn>
          <SpacedColumn>
            {benefitsText.map(({ heading, text }) => (
              <InfoParagraph heading={heading} text={text} />
            ))}
          </SpacedColumn>
        </ResponsiveTwoColumnLayout>
      </Container>

      <StoriesCarousel stories={storiesForCarousel} />

      <Container>
        <SectionIntro
          primaryHeading="How they work"
          secondaryHeading="Riparide Stories"
          paragraphText="Save stays and local adventures, then build your soul-fulfilling escape."
        />
        <IllustratedSteps steps={howItWorksSteps} />
        <PageCTALink
          href="/listings?type=accommodation"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book Your Escape
        </PageCTALink>
        {/* <FAQ items={faqItems} /> */}
      </Container>
    </>
  )
}

export default StoriesLandingCustomer
